import React, { useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiLayers, FiCopy, FiCheck } from 'react-icons/fi';
import { useWorkloads } from './hooks/useWorkloads';
import WorkloadModal from './components/WorkloadModal';
import { useFilter } from '../../../contexts/FilterContext';
import type { WorkloadSummary, WorkloadCreate, WorkloadUpdate } from '../../../types/workload';

function formatTagSummary(workload: WorkloadSummary): string {
  return (workload.tag_pairs || [])
    .map((tp, i) => {
      const pair = `${tp.tag_key}=${tp.tag_value}`;
      const op = i < workload.tag_pairs.length - 1 && tp.operator ? ` ${tp.operator} ` : '';
      return pair + op;
    })
    .join('');
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const WorkloadsPage: React.FC = () => {
  const { workloads, isLoading, createWorkload, updateWorkload, deleteWorkload } = useWorkloads();
  const { accounts, environments, refreshFilterData } = useFilter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWorkload, setEditingWorkload] = useState<WorkloadSummary | null>(null);
  const [selectedWorkload, setSelectedWorkload] = useState<WorkloadSummary | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleCreate = () => {
    setEditingWorkload(null);
    setIsModalOpen(true);
  };

  const handleEdit = (workload: WorkloadSummary) => {
    setEditingWorkload(workload);
    setIsModalOpen(true);
  };

  const handleDelete = async (workload: WorkloadSummary) => {
    if (window.confirm(`Delete workload "${workload.name}"? This cannot be undone.`)) {
      await deleteWorkload(workload.id);
      refreshFilterData();
      if (selectedWorkload?.id === workload.id) {
        setSelectedWorkload(null);
      }
    }
  };

  const handleSave = async (data: WorkloadCreate | WorkloadUpdate) => {
    if (editingWorkload) {
      await updateWorkload(editingWorkload.id, data as WorkloadUpdate);
    } else {
      await createWorkload(data as WorkloadCreate);
    }
    refreshFilterData();
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Workloads</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Define tag-based resource groupings for filtering and cost tracking
            </p>
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            <FiPlus className="w-4 h-4" />
            Add Workload
          </button>
        </div>

      <div className="flex gap-6">
      {/* Left Panel - Workload List */}
      <div className="flex-1 min-w-0">

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : workloads.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <FiLayers className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-3" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">No workloads defined</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Create a workload to group AWS resources by tags for filtering and cost tracking.
            </p>
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              <FiPlus className="w-4 h-4" />
              Create Your First Workload
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tags</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Costs</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">SDK</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Scope</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Projects</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Modified</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {workloads.map((workload) => (
                  <tr
                    key={workload.id}
                    onClick={() => setSelectedWorkload(workload)}
                    className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      selectedWorkload?.id === workload.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{workload.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 dark:text-gray-300 font-mono truncate max-w-xs block">
                        {formatTagSummary(workload)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {workload.cost_tracking_enabled ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 rounded-full">
                          Tracked
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 dark:text-gray-500">Off</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <code className="text-xs font-mono text-gray-500 dark:text-gray-400 truncate max-w-[180px] block">
                          set_workload(&quot;{workload.name}&quot;)
                        </code>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleCopy(`tracer.set_workload("${workload.name}")`, `sdk-${workload.id}`); }}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors shrink-0"
                          title="Copy SDK call"
                        >
                          {copiedField === `sdk-${workload.id}` ? <FiCheck className="w-3.5 h-3.5 text-green-500" /> : <FiCopy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {(workload.account_ids?.length || 0) === 0
                          ? 'All Accounts'
                          : `${workload.account_ids.length} account${workload.account_ids.length > 1 ? 's' : ''}`}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {workload.project_ids.length}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(workload.updated_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(workload); }}
                          className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          title="Edit"
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(workload); }}
                          className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Right Panel - Workload Detail */}
      {selectedWorkload && (
        <div className="w-96 shrink-0 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{selectedWorkload.name}</h3>

          {selectedWorkload.description && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{selectedWorkload.description}</p>
          )}

          <div className="space-y-4">
            <div>
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Tag Pairs</h4>
              <div className="space-y-1">
                {(selectedWorkload.tag_pairs || []).map((tp, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <code className="text-sm font-mono text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                      {tp.tag_key}={tp.tag_value}
                    </code>
                    {i < selectedWorkload.tag_pairs.length - 1 && tp.operator && (
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                        tp.operator === 'OR'
                          ? 'text-orange-600 dark:text-orange-400'
                          : 'text-blue-600 dark:text-blue-400'
                      }`}>
                        {tp.operator}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* SDK Integration */}
            <div>
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">SDK Integration</h4>
              <div className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-800/50 rounded-md">
                <code className="text-sm font-mono text-gray-900 dark:text-white truncate">
                  tracer.set_workload(&quot;{selectedWorkload.name}&quot;)
                </code>
                <button
                  onClick={() => handleCopy(`tracer.set_workload("${selectedWorkload.name}")`, 'sdk-workload')}
                  className="ml-2 p-1.5 text-gray-400 hover:text-blue-600 transition-colors shrink-0"
                  title="Copy"
                >
                  {copiedField === 'sdk-workload' ? <FiCheck className="w-4 h-4 text-green-500" /> : <FiCopy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Use this in your code to tag traces with this workload.
              </p>
            </div>

            <div>
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Account Scope</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {(selectedWorkload.account_ids?.length || 0) === 0
                  ? 'All Accounts'
                  : (selectedWorkload.account_names || []).join(', ')}
              </p>
            </div>

            {(selectedWorkload.project_names?.length || 0) > 0 && (
              <div>
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Associated Projects</h4>
                <div className="space-y-1">
                  {selectedWorkload.project_names.map((name, i) => (
                    <p key={i} className="text-sm text-gray-600 dark:text-gray-300">{name}</p>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">Cost Tracking</h4>
              {selectedWorkload.cost_tracking_enabled ? (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 rounded-full">
                  Enabled
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-full">
                  Disabled
                </span>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {selectedWorkload.cost_tracking_enabled
                  ? 'Infrastructure costs are tracked using the tag pairs above.'
                  : 'Enable cost tracking in the workload settings to allocate costs.'}
              </p>
            </div>
          </div>
        </div>
      )}

      <WorkloadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        workload={editingWorkload}
        accounts={accounts}
        environments={environments}
      />
    </div>

    {/* How it works guide */}
    <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">How Workloads Work</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold">1</span>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Define tag pairs</h4>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 ml-8">
            Create a workload with AWS tag key/value pairs that identify your application resources (e.g., <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">Product=ai-platform AND Environment=production</code>).
          </p>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold">2</span>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Tag your AWS resources</h4>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 ml-8">
            Apply the matching tags to your AWS resources (EC2, S3, RDS, etc.) via the AWS Console, CLI, or IaC. Lens will automatically detect tagged resources via Cost Explorer.
          </p>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold">3</span>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">Tag traces with the SDK</h4>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 ml-8">
            Use <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">tracer.set_workload(&quot;name&quot;)</code> in your application code to associate AI agent traces, runs, and evaluations with this workload.
          </p>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Workloads can be linked to projects for cost attribution. Use the global filter in the navbar to view data scoped to a specific workload, project, account, or environment.
        </p>
      </div>
    </div>

    </div>
    </div>
  );
};

export default WorkloadsPage;
